import {
  ColorType,
  CrosshairMode,
  DeepPartial,
  ChartOptions,
  PriceChartOptions,
} from "lightweight-charts";

const baseLayout = {
  background: { type: ColorType.Solid, color: "#ffffff" },
  textColor: "#333333",
  fontFamily: "Tahoma, Arial, sans-serif",
  attributionLogo: false,
} as const;

const baseGrid = {
  vertLines: { color: "#f0f0f0" },
  horzLines: { color: "#f0f0f0" },
} as const;

const baseCrosshair = {
  // Magnet snaps the horizontal crosshair + Y label to the series value,
  // so it matches the tooltip instead of the free mouse Y coordinate.
  mode: CrosshairMode.Magnet,
} as const;

const baseHandleScroll = {
  mouseWheel: true,
  pressedMouseMove: true,
  horzTouchDrag: true,
  vertTouchDrag: true,
} as const;

const baseHandleScale = {
  axisPressedMouseMove: true,
  mouseWheel: true,
  pinch: true,
} as const;

export function getTimeChartOptions(
  overrides: DeepPartial<ChartOptions> = {}
): DeepPartial<ChartOptions> {
  const {
    layout,
    grid,
    crosshair,
    leftPriceScale,
    rightPriceScale,
    timeScale,
    handleScroll,
    handleScale,
    ...rest
  } = overrides;

  return {
    layout: { ...baseLayout, ...layout },
    grid: { ...baseGrid, ...grid },
    crosshair: { ...baseCrosshair, ...crosshair },
    timeScale: {
      borderVisible: false,
      timeVisible: true,
      secondsVisible: true,
      ticksVisible: true,
      visible: true,
      minimumHeight: 32,
      ...timeScale,
    },
    handleScroll:
      typeof handleScroll === "boolean"
        ? handleScroll
        : { ...baseHandleScroll, ...handleScroll },
    handleScale:
      typeof handleScale === "boolean"
        ? handleScale
        : { ...baseHandleScale, ...handleScale },
    ...rest,
    // Force Y axis on the left (after ...rest so nothing can override it)
    defaultVisiblePriceScaleId: "left",
    leftPriceScale: {
      borderVisible: true,
      ticksVisible: true,
      minimumWidth: 60,
      ...leftPriceScale,
      visible: true,
    },
    rightPriceScale: {
      ...rightPriceScale,
      visible: rightPriceScale?.visible ?? false,
    },
  };
}

export function getPriceChartOptions(
  overrides: DeepPartial<PriceChartOptions> = {}
): DeepPartial<PriceChartOptions> {
  const {
    layout,
    grid,
    crosshair,
    leftPriceScale,
    rightPriceScale,
    timeScale,
    handleScroll,
    handleScale,
    ...rest
  } = overrides;

  return {
    layout: { ...baseLayout, ...layout },
    grid: { ...baseGrid, ...grid },
    crosshair: { ...baseCrosshair, ...crosshair },
    timeScale: {
      borderVisible: false,
      ...timeScale,
    },
    handleScroll:
      typeof handleScroll === "boolean"
        ? handleScroll
        : { ...baseHandleScroll, ...handleScroll },
    handleScale:
      typeof handleScale === "boolean"
        ? handleScale
        : { ...baseHandleScale, ...handleScale },
    ...rest,
    // Force Y axis on the left (after ...rest so nothing can override it)
    defaultVisiblePriceScaleId: "left",
    leftPriceScale: {
      borderVisible: true,
      ticksVisible: true,
      minimumWidth: 60,
      ...leftPriceScale,
      visible: true,
    },
    rightPriceScale: {
      ...rightPriceScale,
      visible: rightPriceScale?.visible ?? false,
    },
  };
}
